import React, { useState, useEffect, useRef } from "react";
import { Send, X } from "lucide-react";
import elliemaeApi from "../../services/interceptors/elliemaeApi";
import "./ConditionComments.css";

const ConditionComments = ({ loanId, conditionId, getToken, onClose }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchComments = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await elliemaeApi.get(
        `/encompass/v3/loans/${loanId}/conditions/${conditionId}/comments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(response.data || []);
    } catch (err) {
      console.error("Error fetching comments:", err);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [conditionId]);

  useEffect(() => {
    if (!loading && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      inputRef.current?.focus();
    }
  }, [loading]);

  const handleSend = async () => {
    if (!newComment.trim() || sending) return;
    try {
      setSending(true);
      const token = await getToken();
      await elliemaeApi.patch(
        `/encompass/v3/loans/${loanId}/conditions/${conditionId}/comments?action=add&view=entity`,
        [{ comments: newComment.trim() }],
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment("");
      await fetchComments();
    } catch (err) {
      console.error("Error posting comment:", err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="cond-comments-panel" ref={panelRef}>
      <div className="cond-comments-panel-header">
        <span className="cond-comments-panel-title">Comments</span>
        <button className="cond-comments-panel-close" onClick={onClose}>
          <X size={16} />
        </button>
      </div>

      <div className="cond-comments-messages">
        {loading ? (
          <div className="cond-comments-loading">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="cond-comments-empty">No comments yet</div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="cond-comments-item">
              <div className="cond-comments-item-header">
                <span className="cond-comments-item-author">
                  {comment.addedBy?.entityName || "Unknown"}
                </span>
                <span className="cond-comments-item-time">
                  {formatDate(comment.addedDate)}
                </span>
              </div>
              <p className="cond-comments-item-text">{comment.comments}</p>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="cond-comments-input-area">
        <div className="cond-comments-input-wrapper">
          <textarea
            ref={inputRef}
            className="cond-comments-input"
            placeholder="Type a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value.slice(0, 300))}
            onKeyDown={handleKeyDown}
            maxLength={300}
            rows={1}
          />
          <span className="cond-comments-char-count">{newComment.length}/300</span>
        </div>
        <button
          className="cond-comments-send-btn"
          onClick={handleSend}
          disabled={!newComment.trim() || sending}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};

export default ConditionComments;
